import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"

import { MOBILE_SECTIONS, MobileField, MobileSection } from "./config"
import { GET_MOBILE_CONFIG, MobileConfigItem } from "./queries"
import { ALLOWED_MODULES_KEY, IS_SUPER_ADMIN_KEY } from "../rest-api/queries/me"

export interface ResolvedSection extends MobileSection {
  /** Sozlama qo'llangandan keyin ko'rinadigan ustunlar */
  visibleFields: MobileField[]
  pageSize: number
  sortOrder: number
}

/** Foydalanuvchiga ochiq bo'lim kodlari */
function allowedCodes(): string[] | null {
  try {
    if (localStorage.getItem(IS_SUPER_ADMIN_KEY) === "1") return null // hammasi
    const raw = localStorage.getItem(ALLOWED_MODULES_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed) || parsed.length === 0) return null
    return parsed
  } catch {
    return null
  }
}

/**
 * Mobil ilovada ko'rinadigan bo'limlar.
 *
 * Uch narsa birlashtiriladi:
 *   1. kodda yozilgan ta'rif (qanday ustunlar bo'lishi mumkin),
 *   2. admin sozlamasi (nimani yashirish kerak),
 *   3. foydalanuvchi huquqlari (nimaga umuman ruxsat bor).
 *
 * Huquq eng ustun turadi: sozlamada yoqilgan bo'lsa ham, ruxsat
 * bo'lmasa bo'lim ko'rinmaydi.
 */
export default function useMobileSections() {
  const { data: config = [], isLoading } = useQuery<MobileConfigItem[]>(
    ["MOBILE_CONFIG"],
    GET_MOBILE_CONFIG,
    {
      // Sozlama kam o'zgaradi — har ochilishda qayta so'ramaymiz
      staleTime: 5 * 60 * 1000,
      retry: false,
    }
  )

  const sections = useMemo<ResolvedSection[]>(() => {
    const codes = allowedCodes()
    const byCode = new Map(config.map((c) => [c.sectionCode, c]))

    const list = MOBILE_SECTIONS.map((section, index) => {
      const cfg = byCode.get(section.code)
      const hidden = new Set(cfg?.hiddenFields || [])

      const visibleFields = section.fields.filter((f) => {
        if (hidden.has(f.key)) return false
        // Sozlama bo'lmasa standart holat ishlaydi
        if (!cfg) return f.defaultVisible !== false
        return true
      })

      return {
        ...section,
        inTabBar: cfg ? cfg.inTabBar : !!section.inTabBar,
        visibleFields: visibleFields.length > 0 ? visibleFields : section.fields.filter((f) => f.primary),
        pageSize: cfg?.pageSize && cfg.pageSize > 0 ? cfg.pageSize : 20,
        sortOrder: cfg?.sortOrder ?? index,
        __enabled: cfg ? cfg.enabled : true,
      } as ResolvedSection & { __enabled: boolean }
    })

    return list
      .filter((s: any) => s.__enabled)
      .filter((s) => (codes ? codes.includes(s.permission) : true))
      .sort((a, b) => a.sortOrder - b.sortOrder)
  }, [config])

  const tabs = useMemo(() => sections.filter((s) => s.inTabBar).slice(0, 3), [sections])

  return { sections, tabs, isLoading }
}
